import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { FilterSelect, DatePicker, SimpleSelect, FormField } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui';

import { type ConsignmentSummary } from '@/entities/consignments';

import { useLocationSelect, getStartingPointTypeOptions, type ConsignmentFormValues } from '../model';
import { SrfUserEntryNote } from './srf-user-entry-note';

interface StartingPointCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
  consignmentData?: ConsignmentSummary;
  isReadOnly?: boolean;
}

export function StartingPointCard({ form, consignmentData, isReadOnly = false }: StartingPointCardProps) {
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const startingPointType = watch('startingPointType');
  const arrivingTo = watch('arrivingTo');

  const initialLocationOption: SelectOption | undefined = useMemo(() => {
    if (!consignmentData?.arrivalLocation?.id || !consignmentData?.arrivalLocation?.description) return undefined;
    return {
      value: consignmentData.arrivalLocation.id,
      label: consignmentData.arrivalLocation.description
    };
  }, [consignmentData?.arrivalLocation]);

  const { locationOptions, isLoading, onSearch, resetSearch } = useLocationSelect({
    filterType: startingPointType,
    selectedValue: arrivingTo,
    initialOption: initialLocationOption
  });

  const startingPointOptions = useMemo(() => getStartingPointTypeOptions(t), [t]);

  const handleStartingPointTypeChange = useCallback(
    (value: ConsignmentFormValues['startingPointType']) => {
      if (value === startingPointType) return;
      setValue('startingPointType', value, { shouldValidate: true });
      setValue('arrivingTo', '');
      resetSearch();
    },
    [resetSearch, setValue, startingPointType]
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.arrivalident.label')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FormField name="startingPointType" error={errors.startingPointType?.message} required>
            <Controller
              name="startingPointType"
              control={control}
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['startingPointType']>
                  value={field.value}
                  onChange={handleStartingPointTypeChange}
                  options={startingPointOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <SrfUserEntryNote section="startingPoint" />

          <FormField label={t('newcmr.arrivingAt.label')} name="arrivingTo" error={errors.arrivingTo?.message} required>
            <Controller
              name="arrivingTo"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  options={locationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onInputValueChange={onSearch}
                  placeholder={isLoading ? t('common.loading') : t('gen.autocomplete.title')}
                  emptyMessage={t('dashboard.locations.empty')}
                  className="w-full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <FormField
            label={t('newcmr.arrivalDate.label')}
            name="expectedArrivalDate"
            error={errors.expectedArrivalDate?.message}>
            <Controller
              name="expectedArrivalDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => field.onChange(date?.toISOString())}
                  dateFormat="dd/MMM/yyyy"
                  id="expectedArrivalDate"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <FormField label={t('newcmr.docno.label')} name="documentNo" error={errors.documentNo?.message}>
            <Input id="documentNo" {...register('documentNo')} placeholder="" disabled={isReadOnly} />
          </FormField>

          <FormField label={t('request.arrivalContact.label')} name="contactInfo" error={errors.contactInfo?.message}>
            <Input id="contactInfo" {...register('contactInfo')} placeholder="" disabled={isReadOnly} />
          </FormField>

          <FormField
            label={t('request.arrivalInstructions.label')}
            name="instructions"
            error={errors.instructions?.message}>
            <Textarea id="instructions" {...register('instructions')} placeholder="" rows={3} disabled={isReadOnly} />
          </FormField>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
